import React, { useState, useEffect } from 'react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import { CONNECTION_TYPES } from '../config/connectionTypes';
import { X, Trash2, Save, Info, Check, FileText, AlertTriangle, Link, Unlink, Loader } from 'lucide-react';
import { ConnectionIcon } from '../utils/iconUtils';
import './DatabaseConnectionDialog.css';

/**
 * Generic Connection Dialog
 *
 * Supports dynamic connection configurations for different systems (Odoo, Jira, etc.)
 * Uses centralized connection type configuration from connectionTypes.js
 *
 * For OAuth connections, the flow is:
 *   1. Fill in connection name, config fields, and API credentials
 *   2. Save Connection (writes config.json + credentials to DynamoDB)
 *   3. Connect (initiates OAuth flow — only available after saving)
 */

const GenericConnectionDialog = ({
  isOpen,
  onClose,
  user,
  selectedWorkspace,
  onConfigSave,
  connectionType,
  connectionId,  // Unique ID for this connection instance
  existingConfig // Existing config for editing
}) => {
  const { organization } = useOrganization();
  const clerk = useClerk();

  const schema = CONNECTION_TYPES[connectionType];

  // Dynamic form state
  const [formData, setFormData] = useState({});
  const [connectionName, setConnectionName] = useState('');

  // SSL/Certificate state (for database connections)
  const [useSsl, setUseSsl] = useState(false);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePath, setCertificatePath] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');

  // OAuth state
  const [oauthConnected, setOauthConnected] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthChecking, setOauthChecking] = useState(false);
  const [oauthCredsSaved, setOauthCredsSaved] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Initialize form data from schema or existing config
  useEffect(() => {
    if (schema) {
      const initialData = {};
      schema.fields.forEach(field => {
        if (field.type === 'select' && field.options?.length > 0) {
          initialData[field.key] = existingConfig?.[field.key] || field.options[0].value;
        } else {
          initialData[field.key] = existingConfig?.[field.key] || '';
        }
      });
      setFormData(initialData);
      setConnectionName(existingConfig?.connection_name || '');

      // SSL certificate info (for database connections)
      if (schema.supportsSSL && existingConfig?.ssl?.root_cert) {
        setUseSsl(true);
        setCertificatePath(existingConfig.ssl.root_cert);
        const fileName = existingConfig.ssl.root_cert.split('/').pop();
        setUploadedFileName(fileName);
      }
    }
  }, [connectionType, existingConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Skip loading if existingConfig is provided (edit mode)
  useEffect(() => {
    if (isOpen && user && selectedWorkspace?.workspace_id && schema && !existingConfig) {
      loadCurrentConfig();
    }
  }, [isOpen, user, selectedWorkspace?.workspace_id, connectionType, existingConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check OAuth status and load credentials on mount (for OAuth connections)
  useEffect(() => {
    if (isOpen && schema?.isOAuth && schema.oauthProvider) {
      checkOAuthStatus();
      if (existingConfig) {
        loadOAuthCredentials();
      }
    }
  }, [isOpen, connectionType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for OAuth callback postMessage
  useEffect(() => {
    if (!schema?.isOAuth) return;

    const handleMessage = (event) => {
      if (event.data?.type === 'oauth_callback' && event.data?.provider === schema.oauthProvider) {
        if (event.data.status === 'success') {
          setOauthConnected(true);
          setSuccessMessage('OAuth connection established!');
        } else {
          setError(`OAuth failed: ${event.data.error || 'Unknown error'}`);
        }
        setOauthLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadCurrentConfig = async () => {
    if (!selectedWorkspace?.workspace_id) return;

    setIsLoading(true);
    setError(null);
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/config?workspace_id=${selectedWorkspace.workspace_id}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load configuration');
      }

      const data = await response.json();
      const config = data.config;

      // Check both legacy format and new connections format
      let connectionConfig = null;
      if (config.connections && config.connections[connectionType]) {
        connectionConfig = config.connections[connectionType];
      } else if (connectionType === 'database' && config.database) {
        connectionConfig = config.database;
      }

      // Populate form from config
      if (connectionConfig) {
        const newFormData = {};
        schema.fields.forEach(field => {
          newFormData[field.key] = connectionConfig[field.key] || '';
        });
        setFormData(newFormData);
      }
    } catch (err) {
      setError(err.message || 'Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const checkOAuthStatus = async () => {
    if (!schema?.oauthProvider) return;
    setOauthChecking(true);
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) return;

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/oauth/status/${schema.oauthProvider}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOauthConnected(data.connected === true);
      }
    } catch (err) {
      console.error('Failed to check OAuth status:', err);
    } finally {
      setOauthChecking(false);
    }
  };

  const loadOAuthCredentials = async () => {
    if (!schema?.oauthProvider) return;
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) return;

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/oauth/credentials/${schema.oauthProvider}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.credentials) {
          setFormData(prev => ({
            ...prev,
            oauth_client_id: data.credentials.client_id || '',
          }));
          setOauthCredsSaved(data.credentials.has_secret === true);
        }
      }
    } catch (err) {
      console.error('Failed to load OAuth credentials:', err);
    }
  };

  const saveOAuthCredentials = async (sessionToken) => {
    if (!schema?.oauthProvider) return true;

    const clientId = (formData.oauth_client_id || '').trim();
    const clientSecret = (formData.oauth_client_secret || '').trim();

    // If no secret provided and credentials are already saved, skip
    if (!clientSecret && oauthCredsSaved && clientId) return true;

    if (!clientId) return true; // No credentials to save

    const orgSlug = getOrgSlug(organization);
    const apiBaseUrl = getApiBaseUrl(orgSlug);

    const body = { client_id: clientId };
    if (clientSecret) {
      body.client_secret = clientSecret;
    }

    const response = await fetch(`${apiBaseUrl}/oauth/credentials/${schema.oauthProvider}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.detail || 'Failed to save API credentials');
    }

    setOauthCredsSaved(true);
    setFormData(prev => ({ ...prev, oauth_client_secret: '' }));
    return true;
  };

  const handleOAuthConnect = async () => {
    if (!schema?.oauthProvider) return;
    setOauthLoading(true);
    setError(null);
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) throw new Error('Unable to get authentication token');

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const params = new URLSearchParams();
      const userScopes = (formData.scopes || '').trim();
      if (userScopes) params.set('scopes', userScopes);
      const userRegion = (formData.region || '').trim();
      if (userRegion) params.set('region', userRegion);
      const qs = params.toString();
      let authorizeUrl = `${apiBaseUrl}/oauth/authorize/${schema.oauthProvider}${qs ? '?' + qs : ''}`;

      const response = await fetch(authorizeUrl, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to initiate OAuth');
      }

      const data = await response.json();
      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        data.authorization_url,
        'oauth_popup',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
    } catch (err) {
      setError(err.message || 'Failed to start OAuth flow');
      setOauthLoading(false);
    }
  };

  const handleOAuthDisconnect = async () => {
    if (!schema?.oauthProvider) return;
    if (!window.confirm(`Disconnect from ${schema.name}?`)) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) throw new Error('Unable to get authentication token');

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/oauth/connections/${schema.oauthProvider}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      });
      if (!response.ok) throw new Error('Failed to disconnect');

      setOauthConnected(false);
      setSuccessMessage('Disconnected successfully');
    } catch (err) {
      setError(err.message || 'Failed to disconnect');
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const validateForm = () => {
    for (const field of schema.fields) {
      // For OAuth credentials: require client_id always; require client_secret only if not already saved
      if (field.isOAuthCredential && field.key === 'oauth_client_secret' && oauthCredsSaved) continue;

      const value = formData[field.key];
      if (field.required && (!value || String(value).trim() === '')) {
        return `${field.label} is required`;
      }
    }

    // Validate URL fields
    for (const field of schema.fields) {
      if (field.isOAuthCredential) continue;
      if (field.key.includes('url') && formData[field.key]) {
        const urlValue = String(formData[field.key]);
        if (!urlValue.startsWith('http://') && !urlValue.startsWith('https://')) {
          return `${field.label} must start with http:// or https://`;
        }
      }
    }

    return null;
  };

  const handleDeleteConnection = async () => {
    if (!window.confirm(`Delete this ${schema.name} connection?`)) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Get current config
      const getResponse = await fetch(`${apiBaseUrl}/config?workspace_id=${selectedWorkspace.workspace_id}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!getResponse.ok) {
        throw new Error('Failed to load configuration');
      }

      const data = await getResponse.json();
      const config = { ...data.config };

      // Remove the connection
      if (config.connections && config.connections[connectionId]) {
        delete config.connections[connectionId];
      }

      // Save updated config
      const saveResponse = await fetch(`${apiBaseUrl}/config?workspace_id=${selectedWorkspace.workspace_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config }),
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to delete connection');
      }

      setSuccessMessage('Connection deleted successfully!');

      if (onConfigSave) {
        onConfigSave();
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to delete connection');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveConnection = async () => {
    setError(null);
    setSuccessMessage(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // For OAuth connections, save credentials to DynamoDB first
      if (schema.isOAuth) {
        await saveOAuthCredentials(sessionToken);
      }

      // Get existing config to preserve other connections
      let existingFullConfig = {};
      try {
        const getResponse = await fetch(`${apiBaseUrl}/config?workspace_id=${selectedWorkspace.workspace_id}`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (getResponse.ok) {
          const getData = await getResponse.json();
          existingFullConfig = getData.config || {};
        }
      } catch (err) {
        console.log('No existing config found, creating new one');
      }

      // Upload certificate first if needed (for database connections)
      let finalCertificatePath = certificatePath;
      if (schema.supportsSSL && certificateFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', certificateFile);

        const uploadResponse = await fetch(`${apiBaseUrl}/config/upload-certificate?workspace_id=${selectedWorkspace.workspace_id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
          },
          body: uploadFormData,
        });

        if (!uploadResponse.ok) {
          const uploadError = await uploadResponse.json();
          throw new Error(uploadError.detail || 'Failed to upload certificate');
        }

        const uploadData = await uploadResponse.json();
        finalCertificatePath = uploadData.certificate_path;
      }

      // Build connection config from form data (exclude OAuth credential fields)
      const connectionConfig = {
        connection_type: connectionType,
        connection_name: connectionName || schema.name,
      };
      if (schema.isOAuth) {
        connectionConfig.oauth_provider = schema.oauthProvider;
      }
      schema.fields.forEach(field => {
        if (field.isOAuthCredential) return;
        const value = formData[field.key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          if (field.key === 'port' && field.type === 'number') {
            connectionConfig[field.key] = parseInt(value);
          } else {
            connectionConfig[field.key] = value;
          }
        }
      });

      // Add SSL config if applicable
      if (schema.supportsSSL && useSsl && finalCertificatePath) {
        connectionConfig.ssl = {
          root_cert: finalCertificatePath
        };
      }

      // Merge with existing config
      const config = {
        ...existingFullConfig,
        connections: {
          ...(existingFullConfig.connections || {}),
          [connectionId || connectionType]: connectionConfig
        }
      };

      // Save config
      const configResponse = await fetch(`${apiBaseUrl}/config?workspace_id=${selectedWorkspace.workspace_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config }),
      });

      if (!configResponse.ok) {
        const configError = await configResponse.json();
        throw new Error(configError.detail || 'Failed to save configuration');
      }

      setSuccessMessage(`${schema.name} connection saved!`);

      if (onConfigSave) {
        onConfigSave({
          id: connectionId,
          type: connectionType,
          name: connectionName || schema.name
        });
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !schema) return null;

  const isEditing = !!existingConfig;
  const regularFields = schema.fields.filter(f => !f.isOAuthCredential);
  const oauthCredentialFields = schema.fields.filter(f => f.isOAuthCredential);

  return (
    <div className="db-connection-overlay">
      <div className="db-connection-dialog">
        <div className="db-connection-header">
          <h2>
            <ConnectionIcon iconName={schema.icon} size={20} />
            {isEditing ? `Edit ${schema.name} Connection` : `Add ${schema.name} Connection`}
          </h2>
          <button
            className="db-connection-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="db-connection-body">
          {isLoading ? (
            <div className="db-loading">
              <div className="db-loading-spinner"></div>
              <span>Loading configuration...</span>
            </div>
          ) : (
            <form className="db-connection-form">
              {schema.description && (
                <div className="db-info-box">
                  <span className="db-info-icon"><Info size={16} /></span>
                  <span className="db-info-text">{schema.description}</span>
                </div>
              )}

              {/* Connection Name — always first */}
              <div className="db-form-row-inline">
                <label htmlFor="connectionName" className="db-form-label">
                  Connection Name
                  <span className="db-form-label-hint">(optional)</span>
                </label>
                <div className="db-form-field">
                  <input
                    id="connectionName"
                    type="text"
                    value={connectionName}
                    onChange={(e) => setConnectionName(e.target.value)}
                    placeholder={`My ${schema.name} Connection`}
                    className="db-form-control"
                  />
                </div>
              </div>

              {/* Regular fields (non-OAuth-credential) */}
              {regularFields.map((field) => (
                field.type === 'checkbox' ? (
                  <div key={field.key} className="db-form-group">
                    <label className="db-form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData[field.key] || false}
                        onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                      />
                      <span>{field.label}</span>
                    </label>
                    {field.help && (
                      <small className="db-form-help db-form-help-checkbox">
                        {field.help}
                      </small>
                    )}
                  </div>
                ) : (
                <div key={field.key} className="db-form-row-inline">
                  <label htmlFor={field.key} className="db-form-label">
                    {field.label}
                    {field.required && <span className="db-form-required">*</span>}
                  </label>
                  <div className="db-form-field">
                    {field.type === 'select' ? (
                      <select
                        id={field.key}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        className="db-form-control"
                      >
                        {field.options?.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={field.key}
                        type={field.type || 'text'}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        className="db-form-control"
                      />
                    )}
                    {field.help && (
                      <small className="db-form-help">{field.help}</small>
                    )}
                  </div>
                </div>
                )
              ))}

              {/* OAuth API Credentials — inline in the form, no separate save button */}
              {schema.isOAuth && oauthCredentialFields.length > 0 && (
                <>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--text-secondary, #9ca3af)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginTop: '8px',
                    marginBottom: '4px',
                  }}>
                    API Credentials
                  </div>
                  {oauthCredentialFields.map((field) => (
                    <div key={field.key} className="db-form-row-inline">
                      <label htmlFor={field.key} className="db-form-label">
                        {field.label}
                        {field.required && <span className="db-form-required">*</span>}
                      </label>
                      <div className="db-form-field">
                        <input
                          id={field.key}
                          type={field.type || 'text'}
                          value={formData[field.key] || ''}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          placeholder={
                            field.key === 'oauth_client_secret' && oauthCredsSaved && !formData[field.key]
                              ? '••••••••  (saved)'
                              : field.placeholder || ''
                          }
                          className="db-form-control"
                        />
                        {field.help && (
                          <small className="db-form-help">{field.help}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* SSL/Certificate Section (for database connections) */}
              {schema.supportsSSL && (
                <>
                  <div className="db-form-group">
                    <label className="db-form-checkbox">
                      <input
                        type="checkbox"
                        checked={useSsl}
                        onChange={(e) => setUseSsl(e.target.checked)}
                      />
                      <span>Use SSL/TLS Connection</span>
                    </label>
                  </div>

                  {useSsl && (
                    <div className="db-certificate-section">
                      <div className="db-form-group">
                        <label htmlFor="certificate">CA Certificate</label>
                        <div className="db-certificate-upload">
                          {uploadedFileName && (
                            <div className="db-certificate-info">
                              <span className="db-cert-icon"><Check size={16} /></span>
                              <div className="db-cert-details">
                                <div className="db-cert-name">{uploadedFileName}</div>
                                <div className="db-cert-path">{certificatePath}</div>
                              </div>
                              <button
                                type="button"
                                className="db-cert-remove"
                                onClick={() => {
                                  setCertificateFile(null);
                                  setCertificatePath('');
                                  setUploadedFileName('');
                                }}
                                title="Remove certificate"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                          <input
                            id="certificate"
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setCertificateFile(file);
                                setUploadedFileName(file.name);
                                setError(null);
                              }
                            }}
                            accept=".pem,.crt,.cert,.key,.cer"
                            className="db-certificate-input"
                          />
                          <label htmlFor="certificate" className="db-certificate-label">
                            <span className="db-cert-upload-icon"><FileText size={16} /></span>
                            {uploadedFileName ? 'Change Certificate' : 'Choose Certificate File'}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* OAuth Status Indicator (only when editing an existing connection) */}
              {schema.isOAuth && isEditing && (
                <div style={{
                  padding: '12px 16px',
                  marginTop: '8px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  borderRadius: '8px',
                  background: oauthConnected ? 'rgba(16, 185, 129, 0.05)' : 'rgba(107, 114, 128, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  {oauthChecking ? (
                    <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : oauthConnected ? (
                    <Check size={14} style={{ color: '#10b981' }} />
                  ) : (
                    <AlertTriangle size={14} style={{ color: '#6b7280' }} />
                  )}
                  <span style={{ fontSize: '13px', color: 'var(--text-primary, #e5e7eb)' }}>
                    {oauthChecking ? 'Checking...' :
                     oauthConnected ? `Connected to ${schema.name}` :
                     'Not connected — save and then authorize'}
                  </span>
                </div>
              )}

              {/* Messages */}
              {error && (
                <div className="db-error-message">
                  <span className="db-error-icon"><AlertTriangle size={16} /></span>
                  <span>{error}</span>
                </div>
              )}

              {successMessage && (
                <div className="db-success-message">
                  <span className="db-success-icon"><Check size={16} /></span>
                  <span>{successMessage}</span>
                </div>
              )}
            </form>
          )}
        </div>

        <div className="db-connection-footer">
          <div className="db-footer-left">
            <button
              className="db-btn db-btn-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            {isEditing && (
              <button
                className="db-btn db-btn-danger"
                onClick={handleDeleteConnection}
                disabled={isLoading || isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="db-loading-spinner-small"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* OAuth Connect/Disconnect — only when editing a saved connection */}
            {schema.isOAuth && isEditing && !oauthChecking && (
              oauthConnected ? (
                <button
                  type="button"
                  className="db-btn db-btn-danger"
                  onClick={handleOAuthDisconnect}
                  style={{ fontSize: '13px' }}
                >
                  <Unlink size={14} />
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="db-btn db-btn-cancel"
                  onClick={handleOAuthConnect}
                  disabled={oauthLoading || !oauthCredsSaved}
                  title={!oauthCredsSaved ? 'Save the connection first to enable authorization' : `Authorize with ${schema.name}`}
                  style={{ fontSize: '13px' }}
                >
                  {oauthLoading ? (
                    <>
                      <div className="db-loading-spinner-small"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link size={14} />
                      Authorize
                    </>
                  )}
                </button>
              )
            )}
            <button
              className="db-btn db-btn-save"
              onClick={handleSaveConnection}
              disabled={isLoading || isSaving}
            >
              {isSaving ? (
                <>
                  <div className="db-loading-spinner-small"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Connection
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericConnectionDialog;
